import ExcelJS from "exceljs";
import { formatCADate } from "../CampaignDetail/utils";
import { ColumnConfig, ReportConfig, ReportOption } from "./definitions";

export const replaceVariableExpressions = (s: string): string => {
  const y = new Date().getFullYear().toString();
  const m = (new Date().getMonth() + 1).toString().padStart(2, "0");
  const d = new Date().getDate().toString().padStart(2, "0");
  return s
    .replace(/\${YEAR\s*-\s*([\d]+)\s*\}/, (_m, d) => (new Date().getFullYear() - parseInt(d)).toString())
    .replace("$YEAR", y)
    .replace("${YEAR}", y)
    .replace("$MONTH", m)
    .replace("${MONTH}", m)
    .replace("$DAY", d)
    .replace("${DAY}", d);
};

export const buildInitialOptions = (options: ReportOption[]): Record<string, string> => {
  return options.reduce((acc: any, option: ReportOption) => {
    const v = option.defaultValue
      ? option.defaultValue
      : option.dataType === "select" && option.options
        ? option.options[0].value
        : "";
    acc[option.name] = replaceVariableExpressions(String(v));
    return acc;
  }, {});
};

export const mergeQueryParams = initialOptions => {
  const searchParams = new URLSearchParams(location.search);
  for (const [key, value] of searchParams.entries()) {
    if (key in initialOptions) {
      // this is not working well with multi-selects, so we need to handle it again in the future
      // leaving it here for now
      if (value.indexOf(",") !== -1) {
        initialOptions[key] = value
          .split(",")
          .map(v => v.trim())
          .filter(v => !!v)
          .map(v => ({ key: "Loading", value: parseInt(v) }));
      } else {
        initialOptions[key] = value;
      }
    }
  }
};

// once data is loaded, check if query params are present and update initialOptions accordingly
export const checkQueryMultiParams = (name: string) => {
  const searchParams = new URLSearchParams(location.search);
  const param = searchParams.get(name);
  if (param) {
    const ids = param
      .split(",")
      .map(v => v.trim())
      .map(v => parseInt(v))
      .filter(v => !!v);
    return ids;
  }
  return [];
};

export const formatValues = (value: any, col?: ColumnConfig) => {
  switch (col?.type) {
    case "date":
      return typeof value === "string" && value.length > 0 ? formatCADate(value) : "";
    case "number":
      return value?.toLocaleString();
    case "currency":
      return typeof value === "number"
        ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value)
        : value;
    case "percent":
      return typeof value === "number" ? `${(value * 100).toFixed(2).replace(/\.?0+$/g, "")} %` : "";
    case "map":
      return col?.options[value] || "";
    case "initials":
      return value
        ? value
            .split(" ")
            .map((n: string) => n[0])
            .join("")
            .toUpperCase()
        : "";
    case "decimal":
    // no op
    default:
      return value;
  }
};

export const downloadBlob = async (blob: Blob, filename: string) => {
  const link = document.createElement("a");
  link.download = filename;
  link.href = URL.createObjectURL(blob);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const buildFile = (report, allData) => {
  const workbook = new ExcelJS.Workbook();

  Object.keys(allData).forEach(key => {
    const [sheetData, columnConfig] = allData[key];

    if (sheetData.length > 0) {
      const worksheet = workbook.addWorksheet(key);

      const headerRow = Object.keys(sheetData[0]);
      const cols = columnConfig || headerRow.map(name => ({ name, type: "string" }) as ColumnConfig) || [];

      worksheet.columns = cols.map((col, index) => {
        return {
          header: col.name,
          key: headerRow[index],
          width: 20,
        };
      });

      worksheet.getRow(1).font = { size: 12, bold: true };

      worksheet.addRows(
        sheetData.map(row => {
          const keys = Object.keys(row);
          keys.forEach((key, colIndex) => {
            if (cols[colIndex]?.type === "date" && row[key]) {
              row[key] = new Date(row[key]);
            }
          });
          return row;
        }),
      );

      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell, colNumber) => {
          let numFmt = null;
          switch (cols[colNumber - 1]?.type) {
            case "date":
              numFmt = "mm/dd/yyyy";
              break;
            case "currency":
              numFmt = '"$"#,##0.00_)';
              // full accounting format if needed
              // numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)'
              break;
            case "percent":
              numFmt = "0.00%";
              break;
            default:
              break;
          }
          cell.numFmt = numFmt;
        });
      });
    }
  });

  return workbook.xlsx.writeBuffer();
};

export const handleDownload = async (report: ReportConfig, allData) => {
  const blob = new Blob([await buildFile(report, allData)], { type: "application/vnd.openxmlformats-officedocument" });
  const filename = `${report.id || "report"}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  downloadBlob(blob, filename);
};

export const range = (min: number, max: number, step: number = 1): number[] => {
  const result: number[] = [];
  for (let i = min; i <= max; i += step) {
    result.push(i);
  }
  return result;
};
